import React from 'react';
import { AlertCircle, Loader2, ChevronLeft, ChevronRight, ArrowLeftRight } from 'lucide-react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useTransactions } from '../../../hooks/useTransactions';
import { getCurrentUserId } from '../../../lib/supabase';
import { useRegion } from '../../../contexts/RegionContext';
import { getCategoryIconComponent } from '../../../utils/icon-utils';
import { categoryService } from '../../../services/category-service';
import type { Category } from '../../../types/category.types';
import { CategoryType } from '../../../types/category.types';
import type { Transaction, TransactionListResponse } from '../../../types/transaction.types';

interface RecentTransactionsCardProps {
  className?: string;
}

const FALLBACK_COLOR = '#1D4ED8';

function hexToRgba(hex: string | undefined, alpha: number): string {
  if (!hex) return `rgba(29, 78, 216, ${alpha})`; // fallback to blue
  let sanitized = hex.trim();
  if (sanitized.startsWith('#')) {
    sanitized = sanitized.slice(1);
  }
  if (sanitized.length === 3) {
    sanitized = sanitized
      .split('')
      .map((char) => char + char)
      .join('');
  }
  if (sanitized.length !== 6) {
    return `rgba(29, 78, 216, ${alpha})`;
  }
  const r = Number.parseInt(sanitized.slice(0, 2), 16);
  const g = Number.parseInt(sanitized.slice(2, 4), 16);
  const b = Number.parseInt(sanitized.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return `rgba(29, 78, 216, ${alpha})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function RecentTransactionsCard({ className }: RecentTransactionsCardProps): React.ReactElement {
  const [userId, setUserId] = React.useState<string | null>(null);
  const [categoriesMap, setCategoriesMap] = React.useState<Map<string, Category>>(new Map());
  const [page, setPage] = React.useState(1);
  const navigate = useNavigate();
  const region = useRegion();

  const formatCurrency = React.useMemo(() => {
    if (!region?.formatCurrency) {
      return (amount: number) => {
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      };
    }
    return region.formatCurrency;
  }, [region]);

  React.useEffect(() => {
    getCurrentUserId().then(setUserId);
  }, []);

  // Fetch recent transactions
  const limit = 7;
  const { data: transactionsData, isLoading, isError, error } = useTransactions({
    userId: userId ?? '',
    limit,
    page,
    sortOrder: 'desc',
  });

  const transactions: Transaction[] = (transactionsData as TransactionListResponse | undefined)?.transactions || [];
  const total = (transactionsData as TransactionListResponse | undefined)?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // Fetch categories for all transactions
  React.useEffect(() => {
    if (transactions.length === 0) return;

    const fetchCategories = async () => {
      const categoryIds = transactions
        .map((t) => t.categoryId)
        .filter((id): id is string => !!id);

      if (categoryIds.length === 0) {
        setCategoriesMap(new Map());
        return;
      }

      // Fetch all categories in parallel
      const categoryPromises = categoryIds.map(async (categoryId) => {
        const result = await categoryService.getCategoryById(categoryId);
        if (result.data) {
          return { id: categoryId, category: result.data };
        }
        return null;
      });

      const categoryResults = await Promise.all(categoryPromises);
      const newCategoriesMap = new Map<string, Category>();

      categoryResults.forEach((result) => {
        if (result) {
          newCategoriesMap.set(result.id, result.category);
        }
      });

      setCategoriesMap(newCategoriesMap);
    };

    fetchCategories();
  }, [transactions]);

  const handleViewAll = () => {
    navigate('/transactions');
  };

  return (
    <div className={clsx('card h-full', className)}>
      <div className="card-header" style={{ paddingBottom: '24px' }}>
        <div>
          <div className="flex items-center justify-between">
            <h2 className="card-title">Recent transactions</h2>
            <button
              onClick={handleViewAll}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors whitespace-nowrap"
            >
              View All
            </button>
          </div>
          <p className="card-description">
            Your latest transactions
          </p>
        </div>
      </div>

      <div className="card-content" style={{ paddingTop: '0px' }}>
        {isLoading || userId === null ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" aria-label="Loading transactions" />
          </div>
        ) : isError ? (
          <div className="flex h-64 flex-col items-center justify-center space-y-2 text-center">
            <AlertCircle className="h-8 w-8 text-red-500" aria-hidden="true" />
            <p className="text-sm text-red-600 dark:text-red-400">
              {error instanceof Error ? error.message : 'Unable to load transactions.'}
            </p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center space-y-2 text-center">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-200">No transactions found</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Import transactions to see your recent activity.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {transactions.map((transaction) => {
                const category = transaction.categoryId ? categoriesMap.get(transaction.categoryId) : undefined;
                const categoryName = category?.name || 'Uncategorised';
                const categoryColor = category?.color || FALLBACK_COLOR;
                const isTransfer = category?.type === CategoryType.TRANSFER;
                const Icon = isTransfer 
                  ? null 
                  : getCategoryIconComponent({
                      icon: category?.icon,
                      slug: category?.slug,
                      name: category?.name || 'Uncategorised',
                      type: category?.type || CategoryType.EXPENSE,
                    });

                const backgroundColor = hexToRgba(categoryColor, 0.12);
                const iconColor = categoryColor;

                const displayName = transaction.merchantName || transaction.description || 'Transaction';
                const formattedDate = region.formatDate(transaction.date);

                const isCredit = transaction.type === 'credit';
                const amount = Math.abs(transaction.amount);
                const formattedAmount = formatCurrency(amount);
                const displayAmount = isCredit ? `+${formattedAmount}` : formattedAmount;

                return (
                  <button
                    key={transaction.id}
                    onClick={() => navigate(`/transactions?search=${encodeURIComponent(transaction.id)}`)}
                    className="w-full flex items-center justify-between rounded-lg border border-gray-100 p-3 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer text-left"
                    aria-label={`View transaction: ${displayName}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor }}
                      >
                        {isTransfer ? (
                          <ArrowLeftRight className="h-5 w-5" style={{ color: iconColor }} aria-hidden="true" />
                        ) : Icon ? (
                          <Icon className="h-5 w-5" style={{ color: iconColor }} aria-hidden="true" />
                        ) : (
                          <div className="w-5 h-5 rounded" style={{ backgroundColor: iconColor }} />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{displayName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {categoryName} • {formattedDate}
                        </p>
                      </div>
                    </div>
                    <p
                      className={clsx(
                        'text-sm font-medium',
                        isCredit
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-900 dark:text-gray-100'
                      )}
                    >
                      {displayAmount}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1 || isLoading}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span>Previous</span>
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages || isLoading}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ height: '38px' }}
                  aria-label="Next page"
                >
                  <span>Next</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

